import $ from 'jquery'
import Backbone from 'backbone'
import _ from 'lodash'
import conductor from 'scoreboard/components/conductor'
import DisplayHeader from 'scoreboard/components/display-header'
import errorHandler from 'scoreboard/components/error-handler'
import i18n from 'scoreboard/components/i18n'
import template from './admin-two-column-editor.hbs'
import FormView from 'scoreboard/components/form-view'
import DeleteConfirmation from 'scoreboard/components/delete-confirmation'
import appDisplay from 'scoreboard/components/app-display'
import autofocus from 'scoreboard/polyfills/autofocus'
import logPath from 'scoreboard/components/log-path'
import LoadingDots from 'scoreboard/components/loading-dots'
import constants from 'scoreboard/components/constants'
import mobile from 'scoreboard/components/mobile'
import TwoColumnDetailsForm from 'scoreboard/components/two-column-details-form'
import scrollContainer from 'scoreboard/components/scroll-container'
import layoutManager from 'scoreboard/components/layout-manager'


const AdminTwoColumnEditorFormView = FormView.extend({
	change: function(e) {
		// if the user didn't type in the finder
		if (_.isUndefined(e) || $(e.target).closest('.finder').length === 0) {
			// proceed as normal
			FormView.prototype.change.apply(this, arguments)
		}
	}
})

const CHANGE_ITEM_EVENT = 'change:item'

/**
 * Formerly admin-principals.js
 *
 * This should be used anytime an admin view has right and left column
 * Where the left column has a list or tree to select from (along with any other controls)
 * and the right column allows the selected item on the left to be modified.
 *
 * See subclasses, but this includes: users, groups, dataset rollup trees
 *
 * @property {Object} [loadItemOptions] This can be provided by subclasses and these options
 * will be included on Model.fetch() requests; see this.changeItem()
 *
 * @property {Function} [displayView.doneSaving] If the displayView needs to
 * take action after the model is saved, this function can be implemented which
 * is invoked after a successful model.submit()
 *
 * REQUIREMENTS:
 *
 * 1.) It is up to the SelectorView to trigger CHANGE_ITEM_EVENT
 * when a new selection is made, and add its own guard if necessary.
 */
const AdminTwoColumnEditor = Backbone.View.extend(/** @lends AdminTwoColumnEditor.prototype */{

	// this.Model is the model of the item (e.g. User, Group, DatasetsTree)
	// this.SelectorView is the Backbone view that shows the item selection component (list, tree, etc)
	// this.DisplayView is the Backbone view shown on the right once a principal is selected
	// this.urlRoot is the beginning of the URL as shown in the user's browser, e.g. admin/principals

	tagName: 'form',

	className: 'admin admin-two-column-editor admin-list-view two-column-details-form',

	events: {
		'click .cancel': 'cancel',
		'click .delete': 'delete'
	},

	/**
	 *
	 * @param {Number} [options.itemId] the item to select
	 * @param {Backbone.Model} [options.item] the item that is currently selected/rendered (e.g. User)
	 * @param {FormViewOptions} [options.formViewOptions]
	 *
	 */
	initialize: function(options = {}) {
		this.options = options
		this.itemId = options.itemId
		this.item = options.item

		this.listenTo(Backbone, 'add:domain-object', this._addItem)
		this.listenTo(Backbone, 'delete:domain-object', this._deleteItem)

		this.formView = new AdminTwoColumnEditorFormView(_.extend({
			el: this.el,
			toggleCancel: true,
			submit: this.submit.bind(this)
		}, options.formViewOptions))

		this.on('resize', () => this.twoColumnsDetailsForm.resize())

		_.bindAll(this, '_positionLoadingDots')
	},

	render: function() {
		this.removeSubviews()
		this.$el.empty().append(template())
		this.loadingDots = new LoadingDots({ el: this.$('.loading-dots-container') })

		// show and setup the item list
		this.selectorView = new this.SelectorView(_.extend({
			showImport: !!this.ImportWizard
		}, this.options))

		this.$('.admin-list').append(this.selectorView.el)
		this.selectorView.render()
		logPath('admin-two-column-editor', this.selectorView)
		this.selectorView.on(CHANGE_ITEM_EVENT, item => {
			this.changeItem(item.id)
		})
		this.selectorView.on('add', this.add.bind(this))
		this.selectorView.on('import', this.import.bind(this))
		this.selectorView.on('showList', () => this.twoColumnsDetailsForm.resize())
		// appropriately toggle the edit buttons
		var addUrl = this.urlRoot + '/add'
		if (Backbone.history.fragment == addUrl) {
			// If app is loaded with the `addUrl` attached, we need to make sure
			// `this.twoColumnsDetailsForm` exist before calling the resize method.
			this.setupTwoColumnDetailsForm()
			this.add()
			this.$('.edit-buttons').removeClass('is-not-displayed')
		} else if (!this.itemId) {
			this.$('.edit-buttons').addClass('is-not-displayed')
		}

		// retrieve data for the item list
		$.get({ url: _.result(this.Model.prototype, 'urlRoot') }, (data, status, xhr) => {
			this.selectorView?.setXhr?.(xhr)
			this.selectorView.setItems(data)
			this.twoColumnsDetailsForm.resize()
		})

		// show overview if appropriate
		if (this.itemId) {
			this.changeItem(this.itemId)
		}

		// Append header - groups doesn't use the table-hack layout so we'll need
		// to check to make sure we're adding the header where it needs to be
		var mainBody = this.$('.display-pane-body')[0] || this.$('.scroll-container')[0]
		$(mainBody).before(new DisplayHeader({
			heading: i18n.get(this.heading),
			mobileGearHidden: true
		}).render().el)

		_.result(this, 'secure')

		this.formView.start()

		// This method needs to be called after the rest of the rest of the layout
		// has been rendered, or else it will break some sections like Admin Users.
		this.setupTwoColumnDetailsForm()

		this.twoColumnsDetailsForm.resize()

		return this
	},

	setupTwoColumnDetailsForm: function() {
		this.twoColumnsDetailsForm = new TwoColumnDetailsForm(_.extend({}, {
			el: this.el,
			getScrollContainer: () => {
				if (mobile.isMobileWidth()) {
					return this.$('.display-layout')
				} else {
					return scrollContainer(this.el)
				}
			}
		}, this._getTwoColumnDetailsFormOptions()))

		// We need to make sure we handle adjusting the left pane when we're
		// scrolling in the mobile orientation and when we're not. One listener has
		// already been setup in TwoColumnDetailsForm._setupListeners; figure out
		// which one and setup the other listener here for when we cross from
		// desktop -> mobile or vice versa.
		if (mobile.isMobileWidth()) {
			scrollContainer(this.el)
				.on('scroll', () => this.twoColumnsDetailsForm.adjustLeftPanelScroll())
		} else {
			this.$('.display-layout')
				.on('scroll', () => this.twoColumnsDetailsForm.adjustLeftPanelScroll())
		}

		this.twoColumnsDetailsForm.on('afterLeftPanelResize', () => {
			this._adjustLeftPaneResults()
		})
	},

	// To be overridden in views that extend this one
	_getTwoColumnDetailsFormOptions: function() {
		return {
			topMarginAdjustment: layoutManager.getAppTopMargin() + constants.STANDARD_MARGIN
		}
	},

	_adjustLeftPaneResults: function() {
		var searchResults = this.$('.admin-list-view-search-results')
		// If we're adjusting things because we've just selected a left panel item
		// item, take stock of the scroll position before resizing the panel which
		// will cause that scroll position to be lost
		var scroll = 0
		if (this.hasSelectedItem) {
			scroll = searchResults.scrollTop()
		}

		// Nix any preexisting height to invoke the natural height of the panel
		searchResults.css('height', '')
		// The results section should take up the entire panel minus the panel-header
		// and the finder
		var panelHeaderHeight = this.$('.left-panel .panel-header').outerHeight()
		var finderHeight = this.$('.left-panel .finder').outerHeight()
		var height = this.$('.left-panel').outerHeight() - panelHeaderHeight - finderHeight
		searchResults.height(height - 4) // minus border adjustment
			.scrollTop(scroll)
		this.hasSelectedItem = false
	},

	renderSelectedItem: function(model) {
		_.invokeMap([this.displayView], 'remove')
		this.displayView = new this.DisplayView(_.extend({
			model,
			formView: this.formView,
			state: _.get(this.displayView, 'state', {})
		}, this.options))
		this.displayView.on('change', this.formView.change.bind(this.formView))
		logPath('item view', this.displayView)
		this.$('.admin-display').empty().append(this.displayView.el)
		this.displayView.render()
		// after rendering a new item, make sure the banner-error isn't
		// visible (keep in mind the has-error class is applied on this.el
		// which isn't changing even if the display view is replaced)
		this.$el.removeClass('has-error')
		this.$('button.delete, .copy').toggle(!model.isNew())
		this.hasSelectedItem = true
		this.twoColumnsDetailsForm.resize()
	},

	// handles user clicking on the add button
	add: function(attrs = {}) {
		this.item = new this.Model(attrs)
		this.originalItem = this.item.cloneDeep()
		this.renderSelectedItem(this.item)
		conductor.navigate(this.urlRoot + '/add')
		this.$('.edit-buttons').removeClass('is-not-displayed')
		this.focusDisplay()
	},

	import: function () {
		var wizard = new this.ImportWizard()
		wizard.start()
	},

	_toggleLoadingDots: function(show) {
		if (show) {
			this.loadingDots.render()
			this.on('resize', this._positionLoadingDots)
			$(window).on('resize', this._positionLoadingDots)
			this._positionLoadingDots()
		} else {
			this.loadingDots.$el.css('height', 0)
			this.loadingDots.clear()
			this.off('resize', this._positionLoadingDots)
			$(window).off('resize', this._positionLoadingDots)
		}
	},

	_positionLoadingDots: function() {
		let height
		let body = $('body')
		let container = mobile.isMobileOrientation() ? this.$('.display-layout') : this.$('.display-pane-body')
		if (body.hasClass('display-small') || body.hasClass('display-medium')) {
			// the sides are stacked upon one another, take that into account
			height = container.height()/2
		} else {
			height = container.height()
		}
		this.loadingDots.$el.css('height', height - constants.STANDARD_MARGIN*2)
	},

	/**
	 * Loads the item with the given id.
	 * Assigns the jqXHR to `this.itemPromise`
	 */
	changeItem: function(itemId) {
		if (this.itemPromise && this.itemId == itemId) {
			// Don't reload the same item multiple times
			return
		}
		const requiresNav = this.requiresNavOnChangedItem(itemId)
		this.itemId = itemId
		this.item = new this.Model({ id: itemId })
		this.originalItem = this.item.cloneDeep()
		this.$('.admin-display').empty()
		this._toggleLoadingDots(true)
		this.itemPromise = this.item.fetch({
			data: this.loadItemOptions,
			success: model => {
				this._toggleLoadingDots(false)
				this.originalItem = this.item.cloneDeep()
				// show the buttons before calling renderItem, in case
				// renderItem determines the buttons should be hidden
				// instead (e.g. based on permission to edit the user)
				this.$('.edit-buttons').removeClass('is-not-displayed')
				this.renderSelectedItem(model)
				requiresNav && conductor.navigate(this.urlRoot + '/' + itemId)
				// needed or else the cancel button doesn't work after changing
				// items
				this.setElement(this.el)
				this.formView.start()
				this.focusDisplay()
			},
			error: (model, resp) => errorHandler(resp)
		})
	},

	/**
	 * Determines if conductor.navigate() should be called with the new itemId
	 * @param {Number} itemId
	 *
	 * @returns {boolean}
	 */
	requiresNavOnChangedItem: function(itemId) {
		return true // users and groups should always navigate
	},

	// focuses the first element in the display view
	focusDisplay: function() {
		// for some reason a timeout has to be applied or Chrome doesn't want
		// to change the focus
		setTimeout(function() {
			// Call autofocus which will focus the finder input inside the item list
			autofocus(this.el)
		}.bind(this), 10)
	},

	/**
	 * callback after an item has been added
	 */
	_addItem: function(item) {
		if (!(item instanceof this.Model)) {
			return
		}

		this.model = item
		process.nextTick(() => this._adjustLeftPaneResults())
	},

	/**
	 * callback after an item has been deleted
	 */
	_deleteItem: function(item) {
		if (!(item instanceof this.Model)) {
			return
		}

		if (conductor.id() == item.id) {
			this.model = null
			this.$('.admin-display').empty()
			conductor.navigate(this.urlRoot)
			process.nextTick(() => this._adjustLeftPaneResults())
		}
	},

	cancel: function() {
		this.item = this.originalItem
		this.renderSelectedItem(this.item)
		this.formView.start()
	},

	/**
	 * @returns {Object} Gets the model attributes that will be PUT using
	 * Backbone.save on the current model.
	 */
	gatherData: function() {
		return this.displayView.readFromDom()
	},

	/**
	 * @param [options.navigate=true] pass false to skip navigation
	 * @returns {Promise} fulfilled with the saved model
	 */
	submit: function(options = {}) {
		var event = (this.item.isNew() ? 'add' : 'edit')
			+ ':domain-object'
		this.itemPromise = this.item.save(this.gatherData(), {
			success: () => this.onSaveSuccess(options, event),
			error: this.onSaveError.bind(this)
		})
		return this.itemPromise
	},

	/**
	 * @param [options.navigate=true] pass false to skip navigation
	 * @param event the name of the Backbone event to trigger if the save is successful
	 */
	onSaveSuccess: function(options = {}, event) {
		// #56458 the originalItem needs to be updated to the current item
		// anytime the page is saved
		// #54854 keep track of the originalItem so that we can compare
		// the group type on the next submit. I didn't do a cloneDeep
		// because for this particular issue we're only worried about
		// the group type, and cloneDeep can cause performance issues
		// when large webs of datasets are involved
		this.originalItem = this.item.clone()

		const {navigate=true} = options
		Backbone.trigger(event, this.item)
		this.formView.start({ instadone: true })
		navigate && conductor.navigate(_.result(this, 'urlRoot') + '/' + this.item.id)
		_.result(this.displayView, 'doneSaving')
		this.$('button.delete').show()
		this.$('.copy').show()
		// make sure the delete button will actually work
		this.setElement(this.el)
	},

	onSaveError: function(model, xhr) {
		this.trigger('error', model, xhr)
		this.handleError(model, xhr)
		// after the formView has had a chance to update itself
		this.trigger('error:post-formview', model, xhr)
	},

	handleError: function(model, xhr) {
		errorHandler(xhr, {
			formView: this.formView
		})
	},

	'delete': function() {
		appDisplay.dialog(this.itemPromise, () => {
			const confirmation = this._createDeleteConfirmation()
			confirmation.once('confirm', item => {
				this.item.destroy({
					success: () => {
						Backbone.trigger('delete:domain-object', this.item)
						this.$('.edit-buttons').addClass('is-not-displayed')
						confirmation.trigger('done') // close the confirmation
					}
				})
			})
			return confirmation
		})
	},

	// creates the delete confirmation prompt displayed to the user
	_createDeleteConfirmation: function() {
		// #8972 don't clobber the title of the delete dialog
		var options = _.omit(this.item.attributes, 'title')
		return new DeleteConfirmation(options)
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.removeSubviews()
		this.formView.remove()
		$(window).off('resize', this._positionLoadingDots)
	},

	removeSubviews: function() {
		this.formView.stopListening()
		_.invokeMap([this.selectorView, this.displayView, this.loadingDots], 'remove')
	}

})

export default AdminTwoColumnEditor
export { CHANGE_ITEM_EVENT }

AdminTwoColumnEditor.__filename__ = 'admin/admin-two-column-editor.js'
